import {
  useQuery,
  useInfiniteQuery,
  useQueryClient,
  type QueryKey,
} from '@tanstack/react-query';
import { useEffect, useRef, useCallback, useMemo } from 'react';
import type { SSEEvent, SSEMarketBody } from '@/libs/sdk/api/sse/type';
import type { SSEApi } from '@/libs/sdk/api/sse';
import type {
  PageParam,
  CustomUseQueryOptions,
  CustomUseInfiniteQueryOptions,
} from '../types';
import { getNextPageParam, getPreviousPageParam } from '../utils';

// Stable key for SSE subscriptions to avoid unnecessary re-renders
const getSubscriptionsKey = (subscriptions: SSESubscription[]): string =>
  subscriptions.map(s => `${s.event}:${(s.topics || []).join(',')}`).join('|');

type SSESubscription = {
  event: SSEEvent;
  topics?: string[];
};

// --- useSSEQuery ---

type UseSSEQueryConfig<TData> = CustomUseQueryOptions<TData> & {
  queryKey: QueryKey;
  queryFn: () => Promise<TData>;
};

/**
 * Wraps useQuery with SSE-driven cache invalidation.
 * Uses invalidateQueries (React Query deduplicates) + debounce instead of direct refetch.
 */
export function useSSEQuery<TData>(
  sse: SSEApi,
  queryConfig: UseSSEQueryConfig<TData>,
  subscriptions: SSESubscription[],
  debounceMs = 300
) {
  const queryClient = useQueryClient();
  const { queryKey, queryFn, ...rest } = queryConfig;
  const query = useQuery({ ...rest, queryKey, queryFn });
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const subsKey = useMemo(() => getSubscriptionsKey(subscriptions), [subscriptions]);
  const subsRef = useRef(subscriptions);
  subsRef.current = subscriptions;
  const queryKeyRef = useRef(queryKey);
  queryKeyRef.current = queryKey;

  // Stable callback that reads queryKey from ref
  const invalidate = useCallback(() => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      queryClient.invalidateQueries({ queryKey: queryKeyRef.current });
    }, debounceMs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryClient, debounceMs]);

  useEffect(() => {
    if (rest.enabled === false) return;

    const subs = subsRef.current;
    for (const sub of subs) {
      const json: SSEMarketBody | undefined = sub.topics
        ? { topics: sub.topics }
        : undefined;
      (sse.addEventListener as any)(sub.event, invalidate, json);
    }
    // On SSE reconnect, invalidate to fill the gap
    (sse.addEventListener as any)('base:connected', invalidate);

    return () => {
      clearTimeout(timerRef.current);
      for (const sub of subs) {
        const json: SSEMarketBody | undefined = sub.topics
          ? { topics: sub.topics }
          : undefined;
        (sse.removeEventListener as any)(sub.event, invalidate, json);
      }
      (sse.removeEventListener as any)('base:connected', invalidate);
    };
  }, [rest.enabled, sse, invalidate, subsKey]);

  return query;
}

// --- useSSEInfiniteQuery ---

type UseSSEInfiniteQueryConfig<TData> = CustomUseInfiniteQueryOptions<TData> & {
  queryKey: QueryKey;
  queryFn: (context: {
    signal?: AbortSignal;
    pageParam: PageParam;
  }) => Promise<TData & PageParam>;
  initialPageParam: PageParam;
};

/**
 * Wraps useInfiniteQuery with SSE-driven cache invalidation.
 */
export function useSSEInfiniteQuery<TData>(
  sse: SSEApi,
  queryConfig: UseSSEInfiniteQueryConfig<TData>,
  subscriptions: SSESubscription[],
  debounceMs = 300
) {
  const queryClient = useQueryClient();
  const { queryKey, queryFn, initialPageParam, ...rest } = queryConfig;
  const query = useInfiniteQuery({
    ...rest,
    queryKey,
    queryFn: ({ signal, pageParam }) =>
      queryFn({ signal, pageParam: pageParam as PageParam }),
    initialPageParam,
    getPreviousPageParam,
    getNextPageParam: getNextPageParam as any,
  });
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const subsKey = useMemo(() => getSubscriptionsKey(subscriptions), [subscriptions]);
  const subsRef = useRef(subscriptions);
  subsRef.current = subscriptions;
  const queryKeyRef = useRef(queryKey);
  queryKeyRef.current = queryKey;

  // Stable callback that reads queryKey from ref
  const invalidate = useCallback(() => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      queryClient.invalidateQueries({ queryKey: queryKeyRef.current });
    }, debounceMs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryClient, debounceMs]);

  useEffect(() => {
    if (rest.enabled === false) return;

    const subs = subsRef.current;
    for (const sub of subs) {
      const json: SSEMarketBody | undefined = sub.topics
        ? { topics: sub.topics }
        : undefined;
      (sse.addEventListener as any)(sub.event, invalidate, json);
    }
    (sse.addEventListener as any)('base:connected', invalidate);

    return () => {
      clearTimeout(timerRef.current);
      for (const sub of subs) {
        const json: SSEMarketBody | undefined = sub.topics
          ? { topics: sub.topics }
          : undefined;
        (sse.removeEventListener as any)(sub.event, invalidate, json);
      }
      (sse.removeEventListener as any)('base:connected', invalidate);
    };
  }, [rest.enabled, sse, invalidate, subsKey]);

  return query;
}
